import { getOverallDocumentTypes } from '@containers/scalable-document-list/document-grouping';
import { DocumentImage } from '@containers/scalable-document-list/document-image';
import type { RequestStatus } from '@models/request-status';
import { IDocumentReviewApiService } from '@services/document-review-api-service';
import { IObjectUrlService } from '@services/object-url-service';
import {
    constant,
    each,
    every,
    keyBy,
    map,
    mapValues
} from 'lodash';
import {
    action,
    computed,
    observable,
    ObservableMap
} from 'mobx';
import {
    actionAsync,
    task
} from 'mobx-utils';
import moment from 'moment';
import {IConfigurationValuesService} from '@services/ConfigurationValuesService';
import { I9DocumentInfoDto } from '@dtos/I9Form';
import TrackingService, { ITrackingService } from '@services/TrackingService';
import { ClientTrackingCategory } from '@models/client-tracking-category';
import { ClientTrackingName } from '@models/client-tracking-name';

interface DocumentInteraction {
    rotation: number;
}

export interface IDocumentReviewStore {
    readonly documents: DocumentImage[];
    readonly documentInteraction: Record<string, DocumentInteraction>;
    readonly confirmedDocuments: Record<string, boolean>;
    readonly confirmedAllDocumentsLegible: boolean;
    readonly allDocumentsConfirmed: boolean;
    readonly allConfirmed: boolean;
    readonly areDocumentsLoaded: boolean;
    readonly needsDocumentsConfirmed: boolean;
    readonly needsLegibilityConfirmed: boolean;
    readonly loadingDocumentsRequestStatus: RequestStatus;
    readonly confirmationRequestStatus: RequestStatus;
    readonly enabledUploadDocuments: boolean;
    readonly documentTypesValidationsAll: I9DocumentInfoDto[];
    readonly workerStatus: string | null;

    loadDocuments(): Promise<void>;
    initialise(): void;
    setDocumentConfirmation(type: string, confirmed: boolean): void;
    setAllDocumentsLegible(legible: boolean): void;
    rotateDocument(id: string, amount: number): void;
    validate(): void;
    cleanupDocuments(): void;
    submitConfirmationAsync(): Promise<void>;
}

export enum DocumentReviewStoreErrors {
    UnrecognisedDocument = 'Did not recognise the given document'
}

const initialRotation = 0;

export class DocumentReviewStore implements IDocumentReviewStore {
    @observable
    documents: DocumentImage[] = [];
    @observable
    documentInteractionMap = new ObservableMap<string, DocumentInteraction>();
    @observable
    confirmedDocumentsMap = new ObservableMap<string, boolean>();
    @observable
    confirmedAllDocumentsLegible: boolean = false;
    @observable
    needsDocumentsConfirmed: boolean = false;
    @observable
    needsLegibilityConfirmed: boolean = false;
    @observable
    loadingDocumentsRequestStatus: RequestStatus = 'inactive';
    @observable
    confirmationRequestStatus: RequestStatus = 'inactive';
    @observable
    documentTypesValidationsAll: I9DocumentInfoDto[] = [];
    @observable
    workerStatus: string | null = '';

    private readonly trackingService: ITrackingService;
    constructor(
        private documentReviewApiService: IDocumentReviewApiService,
        private objectUrlService: IObjectUrlService,
        private configurationValuesService: IConfigurationValuesService,
    ) {
        this.trackingService = new TrackingService();
    }

    @computed
    get enabledUploadDocuments(): boolean {
        return this.configurationValuesService?.enabledUploadDocuments;
    }

    @computed
    get documentInteraction(): Record<string, DocumentInteraction> {
        return this.documentInteractionMap.toPOJO();
    }

    @computed
    get confirmedDocuments(): Record<string, boolean> {
        return this.confirmedDocumentsMap.toPOJO();
    }

    @computed
    get allConfirmed(): boolean {
        return this.allDocumentsConfirmed && this.confirmedAllDocumentsLegible;
    }

    @computed
    get allDocumentsConfirmed(): boolean {
        return every(
            Array.from(this.confirmedDocumentsMap.values()),
            confirmation => confirmation
        );
    }

    @computed
    get areDocumentsLoaded(): boolean {
        return this.documents.length > 0;
    }

    @action.bound
    setDocumentConfirmation(type: string, confirmed: boolean) {
        if (!this.confirmedDocumentsMap.has(type)) {
            throw new Error(DocumentReviewStoreErrors.UnrecognisedDocument);
        }

        this.confirmedDocumentsMap.set(type, confirmed);
    }

    @action.bound
    setAllDocumentsLegible(legible: boolean): void {
        this.confirmedAllDocumentsLegible = legible;
    }

    @action.bound
    rotateDocument(id: string, amount: number): void {
        if (!this.documentInteractionMap.has(id)) {
            throw new Error(DocumentReviewStoreErrors.UnrecognisedDocument);
        }

        const interaction = this.documentInteractionMap.get(id)!;
        interaction.rotation = (interaction.rotation + amount) % 360;
    }

    @action.bound
    validate(): void {
        this.needsDocumentsConfirmed = !this.allDocumentsConfirmed;
        this.needsLegibilityConfirmed = !this.confirmedAllDocumentsLegible;
    }

    @action.bound
    initialise(): void {
        this.needsDocumentsConfirmed = false;
        this.needsLegibilityConfirmed = false;
        this.cleanupDocuments();
        this.confirmedAllDocumentsLegible = false;
        this.documentInteractionMap.clear();
        this.confirmedDocumentsMap.clear();
        this.confirmationRequestStatus = 'inactive';
        this.loadingDocumentsRequestStatus = 'inactive';
    }

    @action.bound
    cleanupDocuments(): void {
        each(this.documents, ({ dataUrl }) => {
            this.objectUrlService.revokeObjectUrl(dataUrl);
        });

        this.documents = [];
    }

    @actionAsync
    loadDocuments = async () => {
        this.loadingDocumentsRequestStatus = 'pending';

        try {
            // todo: add DTO response processing to conform to DocumentImage[] type
            const { currentDocumentEntries } = await task(this.documentReviewApiService.fetchI9DocumentsAsync());
            const documents: DocumentImage[] = await task(Promise.all(
                map(currentDocumentEntries, async (documentId: string, documentType: string) => {
                    const dataUrl = await this.documentReviewApiService.downloadI9DocumentAsync(documentId);
                    return {
                        dataUrl,
                        id: documentId,
                        type: documentType
                    };
                })
            ));

            const documentTypes = map(currentDocumentEntries, (documentId: string, documentType: string) => {
                return documentType;
            });

             const request = { documentTypes, includeDisabled: true };
             const documentTypesValidation = await task(this.documentReviewApiService.getDocumentTypesValidation(request));
             const workerStatus = await task(this.documentReviewApiService.getWorkerStatus());
            this.syncDocumentMetadata(documents);
            this.workerStatus = workerStatus?.i9Section2Status ?? '';
            this.documents = documents;
            this.documentTypesValidationsAll = documentTypesValidation;
            this.loadingDocumentsRequestStatus = 'success';
            this.trackingService.trackEvent(ClientTrackingCategory.LoadDocuments, ClientTrackingName.Success);
        } catch (error) {
            this.loadingDocumentsRequestStatus = 'error';
            this.trackingService.trackEvent(ClientTrackingCategory.LoadDocuments, ClientTrackingName.Error);
        } finally {
            // async finally block doesn't get transpiled properly by babel for storybook at the moment
        }
    };

    @actionAsync
    submitConfirmationAsync = async () => {
        this.confirmationRequestStatus = 'inactive';
        if (!this.allConfirmed) {
            return;
        }

        this.confirmationRequestStatus = 'pending';

        try {
            // required to confirm all document types, including the back sides
            const confirmedDocuments = mapValues(
                keyBy(this.documents, 'type'),
                constant(true)
            );
            await task(
                this.documentReviewApiService.confirmDocuments({
                    confirmedDocuments,
                    confirmedOn: moment().toISOString(),
                    legibleDocumentsConfirmed: this.confirmedAllDocumentsLegible
                })
            );

            this.confirmationRequestStatus = 'success';
        } catch (error) {
            this.confirmationRequestStatus = 'error';
        }
    };

    @action.bound
    private syncDocumentMetadata(documents: DocumentImage[]) {
        this.documentInteractionMap.clear();
        this.confirmedDocumentsMap.clear();

        each(documents, ({ id }) => {
            this.documentInteractionMap.set(id, { rotation: initialRotation });
        });

        each(getOverallDocumentTypes(documents), type => {
            this.confirmedDocumentsMap.set(type, false);
        });
    }
}



// WEBPACK FOOTER //
// ./src/stores/onboarding-review/document-review-store.ts